import { useEffect } from "react";
import { useRouter } from "next/router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { checkLoginStatus } from "src/domains/shared/api/exclusiveLogin/exclusiveLogin.repository";
import type { LoginStatusResult } from "src/domains/shared/api/exclusiveLogin/exclusiveLogin.model";
import { hasCognitoSession } from "src/domains/shared/utils/injectCognitoSession";
import { holdLoginSignOutFetchStop } from "src/domains/shared/utils/exclusiveLogin/exclusiveLoginFetchGate";
import { isLoginClaimPending } from "src/domains/shared/utils/exclusiveLogin/loginClaimPending";
import {
  LOGIN_ROUTE,
  SIGNED_OUT_ROUTE,
  SSO_CALLBACK_ROUTE,
} from "src/domains/shared/utils/exclusiveLogin/loginRoutes";
import { handleLoginSuperseded } from "src/domains/shared/utils/exclusiveLogin/loginSupersededHandler";
import { subscribeLoginTabSignal } from "src/domains/shared/utils/exclusiveLogin/loginTabSignal";

/**
 * 상태 확인 주기. 계약 상한(20초) 안이고, 타이머 지연과 응답 시간을 더해도 "보이는 화면은 밀려난 뒤 30초 안에
 * 로그인 화면으로 바뀐다"는 목표에 여유가 있다 (design D6).
 */
const LOGIN_STATUS_POLL_INTERVAL_MS = 15_000;
const LOGIN_STATUS_QUERY_KEY = ["exclusiveLogin", "status"] as const;

/**
 * 로그인 흐름 화면. 여기서는 상태를 확인하지 않고 탭 신호도 따르지 않는다. 진행 중인 로그인(확인창·SSO 점유)을
 * 다른 탭의 사건이 지우지 않게 하기 위해서다.
 */
const LOGIN_FLOW_PATHNAMES = [LOGIN_ROUTE, SSO_CALLBACK_ROUTE];

/** 이 브라우저에 확인할 로그인이 있는가 — 로컬 세션 키가 있고 점유 대기가 아니다. */
const hasWatchableSession = () => !isLoginClaimPending() && hasCognitoSession();

/**
 * 활성 조건은 렌더 시점 값이라, 그 뒤 다른 탭이 로그아웃했거나 로그인을 시작했을 수 있다. 호출 시점에 다시 보고
 * 확인할 로그인이 없으면 요청하지 않는다(`unknown` 은 화면에 영향이 없다).
 */
const checkWatchableLoginStatus = (): Promise<LoginStatusResult> =>
  hasWatchableSession() ? checkLoginStatus() : Promise.resolve("unknown");

/**
 * 로그아웃을 전해 받은 탭의 정지 상한. 로그아웃하는 탭(`useAuth.signOut`)은 해제(3초 상한) 뒤 `Auth.signOut()` 으로
 * 세션 키를 지우는데, 이 로컬 로그아웃에는 자체 상한이 없다(토큰 폐기 요청의 응답 뒤에 키를 지운다). 해제 상한에 그
 * 로컬 로그아웃을 기다릴 여유를 더한 값이다 — 그 안에 세션 키가 사라지지 않아도 정지를 푼다.
 */
const SIGNED_OUT_STOP_LIMIT_MS = 10_000;

/**
 * 세션 키 대기의 주기 확인 간격. 다른 탭이 지운 키는 `storage` 이벤트로 바로 알지만, 이 탭이 지운 키에는 그 이벤트가
 * 오지 않는다(같은 탭의 쓰기는 그 탭에 `storage` 이벤트를 만들지 않는다). 이 탭도 로그아웃 중일 때(두 탭이 잇달아
 * 로그아웃) 키를 지우는 것은 이 탭의 로컬 로그아웃이라, 이 주기로 알아챈다 — 보이는 탭에서는 정지가 키 삭제보다 이만큼
 * 넘게 남지 않는다(숨은 탭은 브라우저가 타이머를 늦출 수 있다).
 */
const SESSION_KEYS_RECHECK_INTERVAL_MS = 500;

/** 세션 키 대기가 끝난 까닭 — 키가 사라졌다 / 상한까지 키가 남아 있었다 */
type SessionKeysWaitResult = "cleared" | "limitReached";

/**
 * 이 브라우저의 Cognito 세션 키가 사라지기를(어느 탭의 로컬 로그아웃이든) 최대 `limitMs` 동안 기다린다. 사라지면
 * `"cleared"`, 상한까지 남아 있으면 `"limitReached"` 로 끝난다 — 어느 쪽이든 resolve 하고 reject 하지 않는다. 시작할
 * 때 한 번, 그 뒤에는 `storage` 이벤트마다, 그리고 `SESSION_KEYS_RECHECK_INTERVAL_MS`(0.5초)마다 다시 본다.
 */
function waitForSessionKeysCleared(limitMs: number): Promise<SessionKeysWaitResult> {
  return new Promise((resolve) => {
    if (!hasCognitoSession()) {
      resolve("cleared");
      return;
    }
    const finish = (result: SessionKeysWaitResult) => {
      clearTimeout(limit);
      clearInterval(recheck);
      window.removeEventListener("storage", finishIfCleared);
      resolve(result);
    };
    const finishIfCleared = () => {
      if (!hasCognitoSession()) finish("cleared");
    };
    const limit = setTimeout(() => finish("limitReached"), limitMs);
    const recheck = setInterval(finishIfCleared, SESSION_KEYS_RECHECK_INTERVAL_MS);
    window.addEventListener("storage", finishIfCleared);
  });
}

/**
 * 열린 화면의 로그인 감시 (design D6·D8). `LoginSessionWatcher` 가 `_app` 에서 한 번 부른다.
 *
 * - 상태 확인: 보이는 동안 15초마다, 그리고 화면이 다시 보이거나 포커스를 얻거나 네트워크가 복구되면 즉시 상태 API 를
 *   부른다. 숨은 탭은 부르지 않는다. 활성 조건은 셋 모두다: 로컬 세션 키가 있고, 점유 대기가 아니고, 로그인 흐름
 *   화면이 아니다. `superseded` 만 처리기를 부르고, 그 밖의 결과(`unknown` 포함)는 화면에 영향을 주지 않는다.
 * - 탭 신호: 다른 탭의 `superseded` 는 처리기(재전파 없음), `signed-out` 은 백엔드 요청·스트림을 멈추고(관문에
 *   로그아웃 정지를 하나 건다 — 이 브라우저의 세션 키가 사라지면(이 탭이 지웠어도) 그 정지를 풀고, 사라지지 않아도
 *   상한 `SIGNED_OUT_STOP_LIMIT_MS`(10초) 뒤에 푼다) 캐시를 비우고 홈으로 옮긴다.
 *   다시 보일 때 놓친 사건도 따른다(신호 모듈이 재확인한다).
 */
export function useLoginSessionWatcher(): void {
  const router = useRouter();
  const queryClient = useQueryClient();
  const onLoginFlowPage = LOGIN_FLOW_PATHNAMES.includes(router.pathname);

  useQuery(LOGIN_STATUS_QUERY_KEY, checkWatchableLoginStatus, {
    enabled: !onLoginFlowPage && hasWatchableSession(),
    refetchInterval: LOGIN_STATUS_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: false,
    staleTime: 0,
    onSuccess: (status) => {
      if (status === "superseded") void handleLoginSuperseded({ broadcast: true });
    },
  });

  useEffect(
    () =>
      subscribeLoginTabSignal((signal) => {
        if (onLoginFlowPage || isLoginClaimPending()) return;
        if (signal.type === "superseded") {
          void handleLoginSuperseded({ broadcast: false });
        } else if (signal.type === "signed-out") {
          // 로그아웃하는 탭이 해제하는 동안 이 탭의 요청이 해제와 경합하지 않게 멈춘다(스펙 「밀려남과 로그아웃은 같은
          // 오리진의 다른 탭에 전파된다」). 여기서 건 정지만 푼다 — 이 탭도 로그아웃 중이면 그 흐름의 정지는 그쪽이 푼다.
          const releaseSignOutStop = holdLoginSignOutFetchStop();
          void waitForSessionKeysCleared(SIGNED_OUT_STOP_LIMIT_MS).then(() => releaseSignOutStop());
          queryClient.clear();
          void router.replace(SIGNED_OUT_ROUTE);
        }
      }),
    [onLoginFlowPage, queryClient, router]
  );
}
